import * as THREE from "three";

export type RouteNode = {
  position: THREE.Vector3;
  arrivalRadius: number;
  label: string;
  corridorWidth?: number;
  holdTime?: number;
  holdUntilClear?: boolean;
  holdRadius?: number;
  // Radio line shown when the squad secures this node
  callout?: string;
};

export type Waypoint = RouteNode;

// holdRadius rationale: a hold should only block on enemies that can plausibly
// engage the squad (max infantry engage range is 135). Radii much larger than
// that make holds clear-the-whole-map checks and stall the route.
export const ESCORT_ROUTE: RouteNode[] = [
  { position: new THREE.Vector3(-400, 0, -350), arrivalRadius: 20, label: "ALPHA", corridorWidth: 24 },
  { position: new THREE.Vector3(-150, 0, -100), arrivalRadius: 20, label: "BRAVO", corridorWidth: 24, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(100,  0,  80),  arrivalRadius: 20, label: "CHARLIE", corridorWidth: 26, holdUntilClear: true, holdRadius: 160 },
  { position: new THREE.Vector3(350,  0,  300), arrivalRadius: 20, label: "DELTA", corridorWidth: 28 },
];

export const ESCORT_PATH: Waypoint[] = ESCORT_ROUTE;

// IRON MARKET (map03): a Delta element moving a controlled corridor through
// the city — staging lot, up the market street, across the plaza, through
// the civic block to a rooftop-covered extraction lot.
export const IRON_MARKET_ROUTE: RouteNode[] = [
  { position: new THREE.Vector3(-380, 0, -330), arrivalRadius: 24, label: "STAGING LOT", corridorWidth: 30 },
  { position: new THREE.Vector3(-240, 0, -180), arrivalRadius: 26, label: "MARKET SOUTH", corridorWidth: 26, holdUntilClear: true, holdRadius: 140 },
  { position: new THREE.Vector3(-90, 0, -60), arrivalRadius: 26, label: "MARKET STREET", corridorWidth: 24, holdUntilClear: true, holdRadius: 140 },
  { position: new THREE.Vector3(30, 0, 20), arrivalRadius: 30, label: "THE PLAZA", corridorWidth: 40, holdUntilClear: true, holdRadius: 160 },
  { position: new THREE.Vector3(160, 0, 140), arrivalRadius: 26, label: "CIVIC BLOCK", corridorWidth: 26, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(310, 0, 280), arrivalRadius: 34, label: "EXTRACTION LOT", corridorWidth: 44 },
];

// GREEN HAMMER (map04): a JSOC element moving a CIA officer through
// insurgent jungle — river landing, up the canopy track, across the ford,
// through the grove village, over the ridge trail to a border-strip LZ.
export const GREEN_HAMMER_ROUTE: RouteNode[] = [
  { position: new THREE.Vector3(-380, 0, -330), arrivalRadius: 26, label: "RIVER LANDING", corridorWidth: 32 },
  { position: new THREE.Vector3(-240, 0, -190), arrivalRadius: 26, label: "CANOPY TRACK", corridorWidth: 26, holdUntilClear: true, holdRadius: 140 },
  { position: new THREE.Vector3(-80, 0, -60), arrivalRadius: 28, label: "THE FORD", corridorWidth: 30, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(60, 0, 60), arrivalRadius: 28, label: "BANANA GROVE", corridorWidth: 34, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(200, 0, 170), arrivalRadius: 26, label: "RIDGE TRAIL", corridorWidth: 26, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(350, 0, 300), arrivalRadius: 36, label: "BORDER LZ", corridorWidth: 48 },
];

// IRON CURTAIN (map05): a NATO asset moving through border farmland —
// assembly farm, along the treelines, through the trench network, past the
// guard post to the wire and a West German crossing point.
export const IRON_CURTAIN_ROUTE: RouteNode[] = [
  { position: new THREE.Vector3(-380, 0, -330), arrivalRadius: 26, label: "ASSEMBLY FARM", corridorWidth: 32 },
  { position: new THREE.Vector3(-230, 0, -180), arrivalRadius: 26, label: "TREELINE ALPHA", corridorWidth: 26, holdUntilClear: true, holdRadius: 140 },
  { position: new THREE.Vector3(-80, 0, -50), arrivalRadius: 28, label: "THE TRENCHES", corridorWidth: 28, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(60, 0, 70), arrivalRadius: 28, label: "GUARD POST", corridorWidth: 30, holdUntilClear: true, holdRadius: 155 },
  { position: new THREE.Vector3(200, 0, 180), arrivalRadius: 26, label: "THE WIRE", corridorWidth: 26, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(350, 0, 300), arrivalRadius: 36, label: "CROSSING POINT", corridorWidth: 48 },
];

// ARCTIC WOLF (map06): a cut-off FSK team moving to a fjord extraction —
// off the ridge, through the pines, across a frozen lake, over the saddle
// and down the fjord road to the shore LZ.
export const ARCTIC_WOLF_ROUTE: RouteNode[] = [
  { position: new THREE.Vector3(-380, 0, -330), arrivalRadius: 26, label: "CUTOFF RIDGE", corridorWidth: 32 },
  { position: new THREE.Vector3(-240, 0, -190), arrivalRadius: 26, label: "PINE HOLLOW", corridorWidth: 26, holdUntilClear: true, holdRadius: 140 },
  { position: new THREE.Vector3(-80, 0, -60), arrivalRadius: 30, label: "FROZEN LAKE", corridorWidth: 36, holdUntilClear: true, holdRadius: 155 },
  { position: new THREE.Vector3(60, 0, 70), arrivalRadius: 28, label: "THE SADDLE", corridorWidth: 28, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(200, 0, 180), arrivalRadius: 26, label: "FJORD ROAD", corridorWidth: 26, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(350, 0, 300), arrivalRadius: 36, label: "FJORD LZ", corridorWidth: 48 },
];

export const RIVER_VALLEY_ROUTE: RouteNode[] = [
  { position: new THREE.Vector3(-620, 0, -410), arrivalRadius: 26, label: "BASE GATE", corridorWidth: 34 },
  { position: new THREE.Vector3(-560, 0, -250), arrivalRadius: 28, label: "VALLEY ENTRY", corridorWidth: 38, holdUntilClear: true, holdRadius: 150 },
  { position: new THREE.Vector3(-450, 0, -95), arrivalRadius: 30, label: "RIVER TRACK", corridorWidth: 42, holdUntilClear: true, holdRadius: 160 },
  { position: new THREE.Vector3(-320, 0, 55), arrivalRadius: 32, label: "BRIDGE OVERWATCH", corridorWidth: 38, holdUntilClear: true, holdRadius: 170 },
  { position: new THREE.Vector3(-215, 0, 185), arrivalRadius: 30, label: "LOW TOWN", corridorWidth: 44, holdUntilClear: true, holdRadius: 160 },
  { position: new THREE.Vector3(-130, 0, 350), arrivalRadius: 38, label: "EXFIL LOT", corridorWidth: 52 },
];
